import type { KitFontId, ThemeTypeStyle } from '../releaseThemes';

import {
  LONG_VIDEO_END_CARD_SEC,
  longVideoEndCardStartSec,
  longVideoEndPose,
  longVideoEndProgress,
} from './longVideoEndCard';
import { WIDE_MIN_TEXT_PX, type WideText } from './lyricVideo';
import { coverFrameHeight, fitStoryLine } from './storyLayout';
import { storyFrameOf } from './storyFrames';
import { clamp01, outCubic, round3 } from './videoMotion';

/**
 * The visualizer (design `asset-redesign/z2 Visualizer - new`, owner answers 2026-10-08): the
 * system of the artwork in motion on a 16:9 stage for the whole song. The full square cover in its
 * theme frame, 16 band-spectrum bars in the theme shape, one id line, motion in whole bars. No ring
 * layout in any theme (Neon and Noir keep their glow). Three poses:
 *
 * - the title card, from 0 s to the end of the first section (at least 8 s, at most 16 s): the
 *   cover on the left, the title as the hero and the artist on the right, the bars under the title;
 * - the stage: the stack in the centre (cover, bars, id line) with three levels from the section
 *   energy (calm, normal, lift: lift opens the bars to 32 across 1240 px and doubles the kick, calm
 *   folds them to 440 px and halves the drift); each change starts on the section start and takes
 *   one bar; no flash;
 * - the end card, from the downbeat at or after the length minus 20 s: the shared end card of the
 *   long videos (`longVideoEndCard.ts`: the stack in the left column, x 120 to 780, with the pill by
 *   date, the SmartLink and the Free watermark); the right half stays free for the YouTube end
 *   screen elements.
 *
 * Every frame is a pure function of the time, so the 30 s render parts stay independent. On Free
 * the watermark shows on every frame. The renderer (`KitVisualizerVideo`) and the web Studio
 * preview read these tables. Pure.
 */

/** The stage level of a section: lift (energy 4 to 5), normal (3), calm (1 to 2). */
export type VisualizerLevel = -1 | 0 | 1;

/** The texts and the timing of one visualizer (`plan.wide`, kind `visualizer`). */
export interface KitVisualizerVideo {
  kind: 'visualizer';
  title: string;
  artist: string;
  cta: string;
  link: string | null;
  /** `pulse`: the cover grows 6 % on the kick (the Pulse style); `drift`: 2.5 %. */
  motion: 'drift' | 'pulse';
  /** One bar of the song (the drift has a period of 4 bars; a move takes one bar). */
  barSec: number;
  /** The title card holds to here (the end of the first section, 8 to 16 s). */
  titleEndSec: number;
  /** The end card starts on this downbeat (at or after the length minus 20 s). */
  endCardSec: number;
  durationSec: number;
  /** The level of each section from its start (video seconds), in time order. */
  levels: Array<{ startSec: number; level: VisualizerLevel }>;
  posterSec: number;
}

/** The title card lasts at least 8 s and at most 16 s (owner answer 3 of page z2). */
export const VISUALIZER_TITLE_MIN_SEC = 8;
export const VISUALIZER_TITLE_MAX_SEC = 16;
/** The poster is the title card, half a second in. */
export const VISUALIZER_POSTER_SEC = 0.5;

/** The level of a section energy (1 to 5). Pure. */
export function visualizerLevelOf(energy: number, emphasis?: 'low' | 'mid' | 'high'): VisualizerLevel {
  const base = energy >= 4 ? 1 : energy <= 2 ? -1 : 0;
  const shift = emphasis === 'high' ? 1 : emphasis === 'low' ? -1 : 0;
  return Math.max(-1, Math.min(1, base + shift)) as VisualizerLevel;
}

/** The end of the title card: the end of the first section, inside 8 to 16 s (and inside the video). Pure. */
export function visualizerTitleEnd(firstSectionEndSec: number | null, durationSec: number): number {
  const wanted = firstSectionEndSec ?? VISUALIZER_TITLE_MIN_SEC;
  return round3(Math.min(Math.max(wanted, VISUALIZER_TITLE_MIN_SEC), VISUALIZER_TITLE_MAX_SEC, durationSec / 3));
}

/**
 * The start of the end card: the shared rule of the long videos (`longVideoEndCardStartSec`: the
 * first downbeat at or after the length minus 20 s). A video under 40 s still ends on the end card,
 * for its last 20 s (or from 0 s). Pure.
 */
export function visualizerEndCardStart(downbeats: ReadonlyArray<number>, durationSec: number): number {
  return longVideoEndCardStartSec(durationSec, downbeats) ?? round3(Math.max(0, durationSec - LONG_VIDEO_END_CARD_SEC));
}

/** The cubic in-out of the moves. */
export function inOutCubic(x: number): number {
  const v = clamp01(x);
  return v < 0.5 ? 4 * v * v * v : 1 - (-2 * v + 2) ** 3 / 2;
}

const lerp = (a: number, b: number, share: number): number => a + (b - a) * share;

/** The stage level at `t`: it moves over one bar from each section start. Pure. */
export function visualizerStageLevel(t: number, video: Pick<KitVisualizerVideo, 'levels' | 'barSec'>): number {
  let previous: number = 0;
  let current: number = 0;
  let start = 0;
  video.levels.forEach((entry, index) => {
    if (t >= entry.startSec) {
      previous = index === 0 ? entry.level : (video.levels[index - 1]?.level ?? entry.level);
      current = entry.level;
      start = entry.startSec;
    }
  });
  return lerp(previous, current, inOutCubic((t - start) / Math.max(0.1, video.barSec)));
}

/** A box in px of 1920 x 1080. */
export interface WideBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** The geometry of the cover of a theme: the frame extension (the Handmade mat) and the Retro record shift. */
export interface VisualizerCoverGeometry {
  ext: number;
  rx: number;
}

/** The cover geometry of a theme at a cover width. Pure. */
export function visualizerCoverGeometry(themeId: string, width: number): VisualizerCoverGeometry {
  return { ext: round3(coverFrameHeight(themeId, width, false) - width), rx: themeId === 'retro' ? -0.24 : 0 };
}

interface Pose {
  cover: { x: number; y: number; w: number };
  bars: { x: number; y: number; w: number; h: number; count: number };
  id: { x: number; y: number; w: number; h: number; o: number; small: boolean };
  title: { x: number; y: number; w: number; h: number; o: number };
  cta: { x: number; y: number; w: number; h: number; o: number };
}

function centrePose(themeId: string, lift: number, calm: number): Pose {
  const cw = 460;
  const { ext, rx } = visualizerCoverGeometry(themeId, cw);
  const bh = lerp(lerp(110, 120, lift), 60, calm);
  const total = cw + ext + 36 + 120 + 28 + 64;
  const top = 140 + (760 - total) / 2;
  const bw = lerp(lerp(640, 1240, lift), 440, calm);
  return {
    cover: { x: 960 - cw / 2 + rx * cw, y: top + ext / 2, w: cw },
    bars: { x: 960 - bw / 2, y: top + cw + ext + 36 + (120 - bh) / 2, w: bw, h: bh, count: lift > 0.5 ? 32 : 16 },
    id: { x: 360, y: top + cw + ext + 36 + 120 + 28, w: 1200, h: 64, o: 1, small: false },
    title: { x: 820, y: 180, w: 980, h: 400, o: 0 },
    cta: { x: 120, y: 760, w: 660, h: 130, o: 0 },
  };
}

function titlePose(themeId: string): Pose {
  const cw = 520;
  const { ext, rx } = visualizerCoverGeometry(themeId, cw);
  return {
    cover: { x: 200 + rx * cw * 0.6, y: 520 - (cw + ext) / 2 + ext / 2, w: cw },
    bars: { x: 830, y: 640, w: 720, h: 110, count: 16 },
    id: { x: 360, y: 836, w: 1200, h: 64, o: 0, small: false },
    title: { x: 820, y: 210, w: 980, h: 390, o: 1 },
    cta: { x: 120, y: 760, w: 660, h: 130, o: 0 },
  };
}

/** The end card: the shared end pose of the long videos (`longVideoEndPose`). */
function endPose(themeId: string, watermark: boolean): Pose {
  const end = longVideoEndPose(themeId, watermark);
  return {
    cover: { x: end.cover.x, y: end.cover.y, w: end.cover.w },
    bars: { ...end.bars, count: 16 },
    id: { ...end.id, o: 1, small: true },
    title: { x: 820, y: 180, w: 980, h: 400, o: 0 },
    cta: { ...end.cta, o: 1 },
  };
}

function mixPose(a: Pose, b: Pose, share: number): Pose {
  return {
    cover: {
      x: lerp(a.cover.x, b.cover.x, share),
      y: lerp(a.cover.y, b.cover.y, share),
      w: lerp(a.cover.w, b.cover.w, share),
    },
    bars: {
      x: lerp(a.bars.x, b.bars.x, share),
      y: lerp(a.bars.y, b.bars.y, share),
      w: lerp(a.bars.w, b.bars.w, share),
      h: lerp(a.bars.h, b.bars.h, share),
      count: share < 0.5 ? a.bars.count : b.bars.count,
    },
    id: {
      x: lerp(a.id.x, b.id.x, share),
      y: lerp(a.id.y, b.id.y, share),
      w: lerp(a.id.w, b.id.w, share),
      h: lerp(a.id.h, b.id.h, share),
      o: lerp(a.id.o, b.id.o, share),
      small: share < 0.5 ? a.id.small : b.id.small,
    },
    title: {
      x: lerp(a.title.x, b.title.x, share),
      y: lerp(a.title.y, b.title.y, share),
      w: lerp(a.title.w, b.title.w, share),
      h: lerp(a.title.h, b.title.h, share),
      o: lerp(a.title.o, b.title.o, share),
    },
    cta: {
      x: lerp(a.cta.x, b.cta.x, share),
      y: lerp(a.cta.y, b.cta.y, share),
      w: lerp(a.cta.w, b.cta.w, share),
      h: lerp(a.cta.h, b.cta.h, share),
      o: lerp(a.cta.o, b.cta.o, share),
    },
  };
}

/** The pose of a visualizer at one time. */
export interface VisualizerPose {
  cover: { x: number; y: number; w: number; h: number; dx: number; dy: number; scale: number };
  bars: WideBox & { count: number; opacity: number };
  id: WideBox & { opacity: number; small: boolean };
  title: WideBox & { opacity: number };
  cta: WideBox & { opacity: number; scale: number };
  /** The haze of the song energy (0..1). */
  haze: number;
  /** -1 (calm) .. 1 (lift). */
  level: number;
  state: 'title' | 'stage' | 'end';
}

/**
 * The pose of a visualizer at the time `t` with the kick and the energy (0..1) of the frame. The end
 * card is the shared end card (`longVideoEndPose`, `longVideoEndProgress`); with `watermark` its CTA
 * group has the Free watermark row. Pure.
 */
export function visualizerPose(
  t: number,
  video: Pick<KitVisualizerVideo, 'levels' | 'barSec' | 'titleEndSec' | 'endCardSec' | 'motion'>,
  themeId: string,
  signals: { kick: number; energy: number },
  watermark = false
): VisualizerPose {
  const bar = Math.max(0.1, video.barSec);
  const level = visualizerStageLevel(t, video);
  const lift = clamp01(level);
  const calm = clamp01(-level);
  const titleP = clamp01((t - video.titleEndSec) / bar);
  const endMove = longVideoEndProgress(t, video.endCardSec, bar);
  const endP = endMove.move;
  const centre = centrePose(themeId, lift, calm);
  const title = titlePose(themeId);
  const end = endPose(themeId, watermark);
  const pose = mixPose(mixPose(title, centre, inOutCubic(titleP)), end, inOutCubic(endP));
  // The bars jump to their new place in the middle of a move (hidden then).
  let bars = titleP < 0.5 ? title.bars : centre.bars;
  if (endP > 0) {
    bars = endP < 0.5 ? centre.bars : end.bars;
  }
  const move = titleP > 0 && titleP < 1 ? titleP : endP > 0 && endP < 1 ? endP : -1;
  const barsOpacity = move < 0 ? 1 : move < 0.5 ? clamp01(1 - 4 * move) : clamp01(4 * move - 3);
  // Whole-bar motion: the drift path has a period of 4 bars; the end card holds it at 30 %.
  const phase = (2 * Math.PI * t) / (4 * bar);
  const held = endP > 0 ? 1 - 0.7 * endP : 1;
  const amp = (video.motion === 'pulse' ? 0.06 : 0.025) + 0.025 * lift;
  const driftScale = 1 + (0.02 * (1 - Math.cos(phase))) / 2;
  const scale = (driftScale + amp * clamp01(signals.kick)) * held + (1 - held);
  const ctaOpacity = endMove.cta;
  const coverHeight = coverFrameHeight(themeId, pose.cover.w, false);
  return {
    cover: {
      x: round3(pose.cover.x),
      y: round3(pose.cover.y),
      w: round3(pose.cover.w),
      h: round3(coverHeight),
      dx: round3(12 * Math.sin(phase) * (1 - 0.5 * calm) * held),
      dy: round3(-8 * Math.sin(2 * phase) * (1 - 0.5 * calm) * held),
      scale: round3(scale),
    },
    bars: {
      ...bars,
      x: round3(bars.x),
      y: round3(bars.y),
      w: round3(bars.w),
      h: round3(bars.h),
      opacity: round3(barsOpacity),
    },
    id: {
      x: round3(pose.id.x),
      y: round3(pose.id.y),
      w: round3(pose.id.w),
      h: round3(pose.id.h),
      opacity: round3(clamp01((pose.id.o - 0.5) * 2)),
      small: pose.id.small,
    },
    title: {
      x: round3(pose.title.x),
      y: round3(pose.title.y),
      w: round3(pose.title.w),
      h: round3(pose.title.h),
      opacity: round3(1 - outCubic(clamp01(titleP * 2))),
    },
    cta: {
      x: round3(pose.cta.x),
      y: round3(pose.cta.y),
      w: round3(pose.cta.w),
      h: round3(pose.cta.h),
      opacity: round3(ctaOpacity),
      scale: round3(0.92 + 0.08 * ctaOpacity),
    },
    haze: round3(clamp01((0.25 + 0.6 * clamp01(signals.energy)) * (1 - 0.6 * calm) + 0.15 * lift)),
    level: round3(level),
    state: endP > 0 ? 'end' : titleP > 0 ? 'stage' : 'title',
  };
}

export interface VisualizerTextsInput {
  video: Pick<KitVisualizerVideo, 'title' | 'artist'>;
  themeId: string;
  fonts: { display: KitFontId; text: KitFontId };
  typeStyle: { display: ThemeTypeStyle & { scale: number }; text: ThemeTypeStyle };
}

/** The fitted texts of a visualizer: the title card and the id line of the stage (the shared end card draws its own). */
export interface VisualizerTexts {
  hero: { title: WideText; artist: WideText };
  id: { titlePx: number; artistPx: number };
}

/**
 * The texts of a visualizer (the build spec of page z2): the title 190 px times the display factor
 * and the artist 62 px times the text factor in x 820 to 1800 with the foot at y 600; the id line
 * 60 / 38 px on the stage (the shared end card draws its id line at 46 / 30 px). Pure.
 */
export function visualizerTexts(input: VisualizerTextsInput): VisualizerTexts {
  const frame = storyFrameOf(input.themeId);
  const dk = frame.factors.display * input.typeStyle.display.scale;
  const tk = frame.factors.text;
  const display = { id: input.fonts.display, style: input.typeStyle.display };
  const text = { id: input.fonts.text, style: input.typeStyle.text };
  const heroTitle = fitStoryLine(input.video.title, display, 190 * dk, 980, true);
  const heroArtist = fitStoryLine(input.video.artist, text, 62 * tk, 980, false);
  const artistPx = Math.max(WIDE_MIN_TEXT_PX, heroArtist.sizePx);
  const titleHeight = heroTitle.sizePx * 1.02 * heroTitle.lines.length;
  const foot = 600;
  const artistTop = foot - artistPx * 1.2;
  const titleTop = artistTop - 18 - titleHeight;
  return {
    hero: {
      title: {
        box: { x: 820, y: round3(Math.max(180, titleTop)), w: 980, h: round3(titleHeight) },
        sizePx: Math.max(WIDE_MIN_TEXT_PX, heroTitle.sizePx),
        lines: heroTitle.lines,
      },
      artist: {
        box: { x: 820, y: round3(artistTop), w: 980, h: round3(artistPx * 1.2) },
        sizePx: artistPx,
        lines: heroArtist.lines,
      },
    },
    id: { titlePx: round3(60 * dk), artistPx: round3(38 * tk) },
  };
}
